/**
 * Shared content components for SEO landing pages.
 *
 * These components render the standard sections every SEO page must include:
 * Breadcrumbs, Hero, How It Works, Why Choose Us, Areas Covered, FAQs,
 * Related Services, Related Locations, CTA.
 *
 * They are content-only — no booking logic, no API calls.
 */
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight, ArrowRight, Phone } from "lucide-react";

// ---- Breadcrumbs ----------------------------------------------------------
export function Breadcrumbs({ items }) {
  return (
    <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb" data-testid="seo-breadcrumbs">
      <ol className="flex items-center gap-1 flex-wrap">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3 text-slate-400" />}
            {i === items.length - 1 ? (
              <span className="text-slate-700 font-medium" aria-current="page">{item.name}</span>
            ) : (
              <Link to={item.url} className="hover:text-orange-500">{item.name}</Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ---- Hero -----------------------------------------------------------------
export function SeoHero({ eyebrow, h1, lead, primaryHref = "/#calculator", primaryLabel = "Get an Instant Quote", secondaryHref = "tel:+447757133163" }) {
  return (
    <section className="relative pt-28 pb-16 md:pt-32 md:pb-20 bg-gradient-to-b from-slate-900 to-slate-800 text-white overflow-hidden" data-testid="seo-hero">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_20%_30%,_rgba(249,115,22,0.4),_transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {eyebrow && (
          <p className="text-orange-400 font-semibold tracking-wide uppercase text-xs mb-4" data-testid="seo-hero-eyebrow">
            {eyebrow}
          </p>
        )}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
          data-testid="seo-hero-h1"
        >
          {h1}
        </h1>
        <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-3xl" data-testid="seo-hero-lead">
          {lead}
        </p>
        <div className="flex flex-wrap gap-4">
          <Link
            to={primaryHref}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition"
            data-testid="seo-hero-cta-primary"
          >
            {primaryLabel} <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={secondaryHref}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3 rounded-lg transition"
            data-testid="seo-hero-cta-call"
          >
            <Phone className="h-4 w-4" /> Call Now
          </a>
        </div>
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-orange-400" /> Fully Insured</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-orange-400" /> 24/7 Booking</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-orange-400" /> Same-Day Available</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-orange-400" /> Transparent Pricing</span>
        </div>
      </div>
    </section>
  );
}

// ---- Intro paragraph ------------------------------------------------------
export function IntroSection({ heading, body }) {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="seo-intro">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
          {heading}
        </h2>
        <p className="text-slate-700 leading-relaxed text-base md:text-lg whitespace-pre-line">{body}</p>
      </div>
    </section>
  );
}

// ---- How It Works ---------------------------------------------------------
export function HowItWorks({ steps }) {
  return (
    <section className="py-12 md:py-16 bg-slate-50" data-testid="seo-how-it-works">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
          How It Works
        </h2>
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <li key={i} className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="w-9 h-9 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-bold mb-3">
                {i + 1}
              </div>
              <h3 className="font-semibold text-slate-900 text-base mb-2">{step.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---- Why Choose Us --------------------------------------------------------
export function WhyChoose({ reasons, heading = "Why Choose Transport Anything 247" }) {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="seo-why-choose">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
          {heading}
        </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reasons.map((r, i) => (
            <div key={i} className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">{r.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{r.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Areas Covered --------------------------------------------------------
export function AreasCovered({ heading, body, areas }) {
  return (
    <section className="py-12 md:py-16 bg-slate-50" data-testid="seo-areas-covered">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
            {heading}
          </h2>
          <p className="text-slate-700 leading-relaxed">{body}</p>
        </div>
        {areas && areas.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {areas.map((area, i) => (
              <span key={i} className="bg-white border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
                {area}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ---- FAQs -----------------------------------------------------------------
export function FaqSection({ faqs, heading = "Frequently Asked Questions" }) {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="seo-faqs">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
          {heading}
        </h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} open={i < 2} className="bg-slate-50 rounded-lg p-4 border border-slate-200 group" data-testid={`seo-faq-${i}`}>
              <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between text-sm sm:text-base">
                {f.q}
                <ChevronRight className="h-4 w-4 text-slate-400 group-open:rotate-90 transition" />
              </summary>
              <p className="mt-3 text-slate-700 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Related Services -----------------------------------------------------
export function RelatedServices({ services, heading = "Related Services" }) {
  if (!services || services.length === 0) return null;
  return (
    <section className="py-12 md:py-16 bg-slate-50" data-testid="seo-related-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
          {heading}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              className="bg-white p-4 rounded-lg border border-slate-200 hover:border-orange-300 hover:shadow-sm transition group"
              data-testid={`seo-related-service-${s.slug}`}
            >
              <p className="font-semibold text-slate-900 text-sm mb-1 group-hover:text-orange-600">{s.name}</p>
              <p className="text-xs text-slate-500 inline-flex items-center gap-1">View service <ArrowRight className="h-3 w-3" /></p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Related Locations ----------------------------------------------------
export function RelatedLocations({ cities, serviceSlug, serviceName, heading }) {
  if (!cities || cities.length === 0) return null;
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="seo-related-locations">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
          {heading || `${serviceName} in Other Cities`}
        </h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {cities.map((c) => (
            <Link
              key={c.slug}
              to={`/${serviceSlug}-${c.slug}`}
              className="text-sm text-slate-700 hover:text-orange-600 inline-flex items-center gap-1 py-1"
              data-testid={`seo-related-location-${c.slug}`}
            >
              <ArrowRight className="h-3 w-3 text-orange-500" />
              {serviceName} in {c.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- CTA ------------------------------------------------------------------
export function CtaSection({ headline = "Ready to book?", subhead = "Get an instant transparent quote in seconds and book in under 60 seconds." }) {
  return (
    <section className="py-12 md:py-20 bg-gradient-to-br from-orange-500 to-orange-600 text-white" data-testid="seo-cta">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "var(--font-heading)" }}>
          {headline}
        </h2>
        <p className="text-orange-50 text-base md:text-lg mb-6 max-w-2xl mx-auto">{subhead}</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/#calculator"
            className="inline-flex items-center gap-2 bg-white text-orange-600 hover:bg-orange-50 font-semibold px-6 py-3 rounded-lg transition"
            data-testid="seo-cta-quote-btn"
          >
            Get Instant Quote <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+447757133163"
            className="inline-flex items-center gap-2 bg-orange-700/40 hover:bg-orange-700/60 border border-white/30 text-white font-semibold px-6 py-3 rounded-lg transition"
            data-testid="seo-cta-call-btn"
          >
            <Phone className="h-4 w-4" /> Call +44 7757 133163
          </a>
        </div>
      </div>
    </section>
  );
}
